import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { ActivityXLSX } from '../../models/dashboard/activity-xlsx';
import { IContractForm } from '../../models/portal/goal/contract.form';
import { ISigningContractForm } from '../../models/portal/goal/signing-contract-form.model';
import { IUser } from '../../models/portal/user.model';
import { GoalTransactionActivitiesModel } from '../../proto/portal-goal.pb';
import { PortalUserGrpcServiceClient } from '../../proto/portal-user.pbsc';
import {
  ContractPreviewEntityRequest,
  ContractSigningRequest,
  UpdateProfilePhotoRequest,
} from '../../proto/portal-user.pb';
import { FileModel } from '../../proto/file-message.pb';
import { Empty } from '@ngx-grpc/well-known-types';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(private portalUserGrpcServiceClient: PortalUserGrpcServiceClient) {}
  /**
   * Preview de contrato para usuario tipo ENTIDAD vía gRPC (lb-legacy → gRPC, BT-6546 Delta 6).
   * Usa ContractPreviewEntity (NO ContractPreview persona-KYC): solo finance_entity_id + responses.
   */
  previewContract(contract: IContractForm): Observable<ISigningContractForm> {
    return this.portalUserGrpcServiceClient
      .contractPreviewEntity(
        new ContractPreviewEntityRequest({
          financeEntityId: contract.financialEntityId,
          responses: JSON.stringify(contract),
        }),
      )
      .pipe(map(r => ({ fileId: r.fileId } as ISigningContractForm)));
  }
  /**
   * Firma de contrato para usuario tipo ENTIDAD vía gRPC (lb-legacy → gRPC, BT-6546 Delta 6).
   * ContractSigning ya soporta entidad (no toca userInformation).
   */
  signingContract(contract: IContractForm): Observable<ISigningContractForm> {
    return this.portalUserGrpcServiceClient
      .contractSigning(
        new ContractSigningRequest({
          financeEntityId: contract.financialEntityId,
          responses: JSON.stringify(contract),
          signature: contract.signature,
        }),
      )
      .pipe(map(r => ({ emailVerified: r.emailVerified } as ISigningContractForm)));
  }
  /**
   * Actualiza la foto de perfil del usuario "me" vía gRPC (lb-legacy → gRPC, BT-6546 Delta 4).
   * El PATCH users/me multipart solo se usaba para la foto: convierte el File a FileModel (bytes)
   * y llama UpdateProfilePhoto. Los callers (bp-user layout 3 apps + profile) siguen llamando
   * update({ photoProfileFile }) sin cambios.
   */
  update(user: IUser): Observable<IUser> {
    const file = user.photoProfileFile;
    if (!file) {
      return throwError(() => new Error('photoProfileFile requerido'));
    }
    return from(file.arrayBuffer()).pipe(
      switchMap(buffer =>
        this.portalUserGrpcServiceClient.updateProfilePhoto(
          new UpdateProfilePhotoRequest({
            photo: new FileModel({
              chunk: new Uint8Array(buffer),
              mimeType: file.type,
              extension: file.name.includes('.') ? file.name.split('.').pop()! : '',
              fileName: file.name,
            }),
          }),
        ),
      ),
      map(userModel => userModel as unknown as IUser),
    );
  }
  /**
   * Reenvía el email con el link para validar el carnet de identidad (lb-legacy → gRPC, BT-6546 Delta 4).
   */
  sendLinkValidateCI(): Observable<boolean> {
    return this.portalUserGrpcServiceClient
      .sendLinkValidateCI(new Empty())
      .pipe(map(r => r.status === 1));
  }
  exportActivitiesToXlxs(activities: GoalTransactionActivitiesModel[]): void {
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(activities.map(t => new ActivityXLSX(t)));
    ws.A1.v = 'Fecha';
    ws.B1.v = 'Detalle';
    ws.C1.v = 'Monto';
    for (let i = 0; i < activities.length; i++) {
      const cellI = i + 2;
      const cell = ws['A' + cellI];
      delete cell.w; // remove old formatted text
      cell.z = 'd/mm/yy'; // set cell format
    }

    /* generate workbook and add the worksheet */
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    /* save to file */
    XLSX.writeFile(wb, 'actividades.xlsx');
  }

  setIntercomChatVisible(visible: boolean = true) {
    (window as any).Intercom('update', { hide_default_launcher: !visible });
  }
}
